import { Grid } from '@mui/material';
import { useContext, useState } from 'react';
import 'moment/locale/en-gb';
import { ReadinessGaugeInstance } from '@esg/esg-global-types';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import ExportDataButton, {
  ExportRequestParameters,
  ExportParametersProps
} from '../ExportDataButton';
import ReadinessGaugeInstanceSelect from '../../shared/input/select/ReadinessGaugeInstanceSelect';
import { readiness_gauge_instance_label } from '../../../lib/readiness_gauge/readiness_gauge_instance';

// Data sent in request to unique Cloud Function.
interface ReadinessGaugeExportRequestParameters extends ExportRequestParameters {
  group_id: string;
  company_id: string;
  readiness_gauge_instance_ids: Array<string>;
}

/**
 * Reusable importing context for metric record file data containing relevant inputs for the microservice.
 * @param {void} handleContextChange Update context in parent from input changes.
 * @param {void} handleContextValid Update context validity in parent based on input changes.
 * @returns {JSX.Element}
 */
const ReadinessGaugeExportParameters = ({ export_type }: ExportParametersProps) => {
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!user || !group || !company) {
    return null;
  }

  // Default parameters state for inputs.
  const initial_parameters: ReadinessGaugeExportRequestParameters = {
    group_id: group.id,
    company_id: company.id,
    readiness_gauge_instance_ids: []
  };

  const [readinessGaugeExportParameters, setReadinessGaugeExportParameters] =
    useState<ReadinessGaugeExportRequestParameters>(initial_parameters);
  const [selectedReadinessGaugeInstances, setSelectedReadinessGaugeInstances] = useState<
    Array<ReadinessGaugeInstance>
  >([]);

  // Validation check for unique export parameters.
  const parameters_valid = Boolean(selectedReadinessGaugeInstances.length);

  const handleReadinessGaugeInstancesChange = (
    readiness_gauge_instances: Array<ReadinessGaugeInstance>
  ): void => {
    const updated_parameters: ReadinessGaugeExportRequestParameters = {
      ...readinessGaugeExportParameters,
      readiness_gauge_instance_ids: readiness_gauge_instances.map((r) => r.id)
    };
    setSelectedReadinessGaugeInstances(readiness_gauge_instances);
    setReadinessGaugeExportParameters(updated_parameters);
  };

  return (
    <Grid container spacing={2}>
      <Grid size={'auto'} sx={{ minWidth: 350 }}>
        <ReadinessGaugeInstanceSelect
          group_id={group.id}
          multi_select={true}
          selected_options={selectedReadinessGaugeInstances}
          input_label={`${readiness_gauge_instance_label.many}`}
          handleChangeReadinessInstances={(
            readiness_gauge_instances: Array<ReadinessGaugeInstance> | ReadinessGaugeInstance | null
          ) => {
            if (Array.isArray(readiness_gauge_instances)) {
              handleReadinessGaugeInstancesChange(readiness_gauge_instances);
            }
          }}
        />
      </Grid>
      <Grid size={'auto'} sx={{ minWidth: 250 }}>
        <ExportDataButton
          user={user}
          export_type={export_type}
          parameters={readinessGaugeExportParameters}
          disabled={!parameters_valid}
          label="Export"
        />
      </Grid>
    </Grid>
  );
};

export default ReadinessGaugeExportParameters;
